import React, { useEffect, useState } from 'react';
import axios from 'axios';
import BaseModal from '../BaseModal';
import { API_BASE_URL } from '../config';
import LineItems from './LineItems';
import { formatMoney, formatMonthLong } from './format';

// The last look before a month is billed: what each company would be invoiced,
// line by line, with nothing saved until the dev confirms.
const IssueModal = ({ month, onIssued, onCancel }) => {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!month) return undefined;
    let cancelled = false;
    setPreview(null);
    setError('');
    setBusy(false);
    setExpanded(null);
    axios
      .get(`${API_BASE_URL}/api/billing/months/${month}/preview`)
      .then((res) => !cancelled && setPreview(res.data))
      .catch((err) => !cancelled && setError(err.response?.data?.msg || 'Could not price that month.'));
    return () => {
      cancelled = true;
    };
  }, [month]);

  if (!month) return null;

  const drafts = preview?.invoices || [];
  const total = drafts.reduce((sum, draft) => sum + draft.total_cents, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!drafts.length || busy) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/billing/months/${month}/issue`, {
        company_ids: drafts.map((d) => d.company_id),
      });
      onIssued(res.data.issued);
    } catch (err) {
      setError(err.response?.data?.msg || 'Could not issue the invoices.');
      setBusy(false);
    }
  };

  return (
    <BaseModal
      open
      titleId="issue-modal-title"
      title={`Issue invoices for ${formatMonthLong(month)}`}
      message="Each company is billed for the month as it stands now. Customers see the invoice on their billing page as soon as it's issued."
      onSubmit={handleSubmit}
      onCancel={onCancel}
      confirmLabel={busy ? 'Issuing…' : drafts.length ? `Issue ${drafts.length} invoice${drafts.length === 1 ? '' : 's'}` : 'Nothing to issue'}
      widthClass="max-w-2xl"
    >
      {!preview && !error && <p className="py-6 text-center text-sm text-gray-400">Pricing the month…</p>}
      {preview && drafts.length === 0 && (
        <p className="py-6 text-center text-sm text-gray-400">Every company on a plan already has an invoice for this month.</p>
      )}
      {drafts.length > 0 && (
        <div className="max-h-[50vh] overflow-y-auto -mx-2 px-2">
          <ul className="divide-y divide-gray-700">
            {drafts.map((draft) => {
              const open = expanded === draft.company_id;
              return (
                <li key={draft.company_id}>
                  <button
                    type="button"
                    onClick={() => setExpanded(open ? null : draft.company_id)}
                    aria-expanded={open}
                    className="w-full flex items-center justify-between gap-3 bg-transparent hover:bg-gray-700/30 px-2 py-3 rounded-md text-left"
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-white truncate">{draft.company_name}</span>
                      <span className="block text-xs text-gray-500">
                        {draft.plan_name} · {draft.usage.seats} seats
                        {draft.discount_percent ? ` · ${draft.discount_percent}% off` : ''}
                      </span>
                    </span>
                    <span className="text-sm font-semibold text-white tabular-nums flex-shrink-0">{formatMoney(draft.total_cents)}</span>
                  </button>
                  {open && (
                    <div className="px-2 pb-3">
                      <LineItems bill={draft} compact />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="flex items-center justify-between border-t border-gray-600 px-2 pt-3 mt-1">
            <span className="text-sm text-gray-300">Total to bill</span>
            <span className="text-base font-semibold text-white tabular-nums">{formatMoney(total)}</span>
          </div>
        </div>
      )}
      {error && <p className="mt-3 text-sm text-red-300" role="alert">{error}</p>}
    </BaseModal>
  );
};

export default IssueModal;
